import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Tooltip } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { iterateGetDeviceMessages } from '../../../../data/NetworkSystem/DeviceMessage/getDeviceMessages';
import { useMQTTChannelMessagesV2 } from '../../../../hooks/useMQTTChannelMessagesV2';
import { useModularTimePeriodSelector } from '../../../../hooks/useModularTimePeriodSelector';
import { ObjectViewType, Severity } from '../../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { DebouncedTextField } from '../../../common/DebouncedTextField';
import { LSCGatewayMessageTable } from '../LSCGatewayMessageTable';
import { columnMap } from '../config/columnMap';
import { useBasicColumVisibility } from '../hooks/useBasicColumnVisibility';
const debug = process.env.REACT_APP_DEBUG && false;
const projectCode = process.env.REACT_APP_PROJECT_CODE;
const stage = process.env.REACT_APP_DEPLOYMENT_STAGE;
export var DisplayMode;
(function (DisplayMode) {
    DisplayMode["LIVE_DATA_ONLY"] = "LIVE_DATA_ONLY";
    DisplayMode["QUERY_ONLY"] = "QUERY_ONLY";
    DisplayMode["LIVE_AND_QUERY"] = "LIVE_AND_QUERY";
})(DisplayMode || (DisplayMode = {}));
export var SelectionFilter;
(function (SelectionFilter) {
    SelectionFilter["SELECTED_ITEMS"] = "SELECTED_ITEMS";
    SelectionFilter["ALL_ITEMS"] = "ALL_ITEMS";
})(SelectionFilter || (SelectionFilter = {}));
export const useNWGatewayDataTable = (props) => {
    const { selectedGateway, initialDisplayMode, initialRangeUnit, initialDuration } = props;
    /** AppMainViewContext  */
    const [{ UserFunctions: userFunctions, contextUser: user, userSelectedGroup }] = useContext(AppMainViewContext);
    const [displayMode, setDisplayMode] = useState(initialDisplayMode ?? DisplayMode.LIVE_DATA_ONLY);
    const [liveDataFilter, setLiveDataFilter] = useState(selectedGateway.length === 0 ? SelectionFilter.ALL_ITEMS : SelectionFilter.SELECTED_ITEMS);
    const [querying, setQuerying] = useState(false);
    const [deviceMessages, setDeviceMessages] = useState([]);
    const [everyNth, setEveryNth] = useState(undefined);
    const deviceMessagesRef = React.useRef(deviceMessages);
    const selectedGatewayHash = selectedGateway.map((o) => o.id).join();
    /** Date range picker state vars */
    const { components: timeSelectorComponents, durationMode, fromDateValue, toDateValue, } = useModularTimePeriodSelector({ rangeUnit: initialRangeUnit ?? 'minutes', rangeLength: initialDuration ?? 30, requireConfirmation: true });
    const maxCols = 40;
    const externalColumnVisibility = Object.fromEntries(Object.keys(columnMap)
        .slice(maxCols)
        .map((k) => [k, false]));
    // state of columns being shown
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: 'networkDataTable',
        objectTypeId: 'networkData',
        viewType: ObjectViewType.List,
        externalColumnVisibility,
    });
    const { controls: ColumnVisibiltyControls } = useBasicColumVisibility({
        setColumnVisibilityModel,
    });
    const displayMessage = (content, type, severity, duration) => {
        const id = v4();
        userFunctions.setUserMessage({ id, content, label: content, type, severity: severity });
        setTimeout(() => userFunctions.dismissUserMessage({ id, content, label: content, type, severity: severity }), duration ?? 3000);
    };
    const addToDeviceMessages = (dm) => {
        debug && console.log(`NW Table view child adding to device messages`, dm);
        setDeviceMessages([dm, ...deviceMessagesRef.current]);
    };
    /**
     * topicGetters are curried as useState will invoke the function on initial render ( without params ) https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
     * @returns
     */
    const networkGatewayTopicGetter = () => (xs) => {
        const gatewayMessages = xs.map((x) => `${projectCode}/${stage}/network/gateway/${x.eui ?? x.id}`);
        return gatewayMessages;
    };
    const [topicGetter, setTopicGetter] = React.useState(networkGatewayTopicGetter);
    const deviceMessagesLength = deviceMessages.length;
    const { setterGetters: [receivedObjects], } = useMQTTChannelMessagesV2({
        user,
        topicGetter,
        topicGenerationObjects: selectedGateway,
        addToParentItems: addToDeviceMessages,
    });
    useEffect(() => {
        debug && console.log('NW Table used effect on ', liveDataFilter);
        setTopicGetter(networkGatewayTopicGetter);
    }, [liveDataFilter]);
    useEffect(() => {
        deviceMessagesRef.current = deviceMessages;
    }, [deviceMessagesLength]);
    useEffect(() => {
        debug && console.log('Timestream use effect');
        if (displayMode !== DisplayMode.LIVE_DATA_ONLY) {
            if (selectedGateway.length > 0 || liveDataFilter === SelectionFilter.ALL_ITEMS) {
                const getNetworkData = async (selectedDeviceSks) => {
                    const params = {
                        userSelectedGroup,
                        deviceEuis: selectedDeviceSks.map((sk) => sk.replace('Device:', '')),
                        fromDateValue,
                        toDateValue,
                        NextToken: undefined,
                    };
                    setQuerying(true);
                    const { err, data, NextToken } = await iterateGetDeviceMessages(0, params);
                    if (data?.length === 0 && NextToken) {
                        console.log(`Timestream returned zero results but a NextToken`);
                    }
                    if (err) {
                        debug && console.log('s3 err in useEffect', err?.message);
                        displayMessage(err.message, 'MessageBar', data ? Severity.warning : Severity.error);
                    }
                    if (data) {
                        //remove existing data or get duplicate keys
                        setDeviceMessages([...data.reverse()]);
                    }
                    else if (err) {
                        console.error('Timestream use effect', err);
                    }
                    else {
                        displayMessage(`No records returned`, 'MessageBar', Severity.info);
                    }
                    setQuerying(false);
                };
                getNetworkData(selectedGateway.map((d) => d.sk));
            }
            else {
                console.log(`WARN: NWDataTable not querying timestream because`, {
                    selectedLength: selectedGateway.length,
                    liveDataFilter,
                });
            }
        }
        else {
            console.log(`TEMOORARILY IN LIVE_DATA_ONLY`);
        }
    }, [fromDateValue, toDateValue, selectedGatewayHash, displayMode]);
    const handleModeChange = (e) => {
        const toggleMode = e.target.value;
        switch (displayMode) {
            case DisplayMode.LIVE_DATA_ONLY:
                if (toggleMode === DisplayMode.QUERY_ONLY)
                    setDisplayMode(DisplayMode.LIVE_AND_QUERY);
                return;
            case DisplayMode.LIVE_AND_QUERY:
                if (toggleMode === DisplayMode.LIVE_DATA_ONLY)
                    setDisplayMode(DisplayMode.QUERY_ONLY);
                if (toggleMode === DisplayMode.QUERY_ONLY)
                    setDisplayMode(DisplayMode.LIVE_DATA_ONLY);
                return;
            case DisplayMode.QUERY_ONLY:
                if (toggleMode === DisplayMode.LIVE_DATA_ONLY)
                    setDisplayMode(DisplayMode.LIVE_AND_QUERY);
                return;
        }
    };
    const handleLiveDataFilterChange = (e) => {
        debug && console.log('NW Table setting liveDataFilter to', e.target.value);
        const filter = e.target.value;
        setLiveDataFilter(filter);
        if (filter === SelectionFilter.ALL_ITEMS) {
            setDisplayMode(DisplayMode.LIVE_DATA_ONLY);
        }
    };
    return {
        Table: (_jsx(_Fragment, { children: _jsx(LSCGatewayMessageTable, { selectedGateway: selectedGateway,
                deviceMessages: [...deviceMessages, ...receivedObjects],
                loading: querying,
                columnVisibilityModel,
                onColumnVisibilityModelChange,
                everyNth }) })),
        LiveDataControl: (_jsxs(FormGroup, { row: true, onChange: handleModeChange, children: [_jsx(FormControlLabel, { label: "Live", value: DisplayMode.LIVE_DATA_ONLY, control: _jsx(Checkbox, { checked: [DisplayMode.LIVE_DATA_ONLY, DisplayMode.LIVE_AND_QUERY].includes(displayMode) }) }, "live"), _jsx(Tooltip, { title: "Select devices first!", arrow: true, disableFocusListener: selectedGateway.length > 0, disableTouchListener: selectedGateway.length > 0, disableHoverListener: selectedGateway.length > 0, children: _jsx("span", { children: _jsx(FormControlLabel, { label: "Recent", value: DisplayMode.QUERY_ONLY, disabled: selectedGateway.length === 0, control: _jsx(Checkbox, { checked: [DisplayMode.QUERY_ONLY, DisplayMode.LIVE_AND_QUERY].includes(displayMode) }) }, "recent") }) })] }, 'liveDataControl')),
        SelectedOrAllControl: (_jsxs(RadioGroup, { value: liveDataFilter, onChange: handleLiveDataFilterChange, row: true, sx: { paddingInlineStart: 2 }, children: [_jsx(FormControlLabel, { label: "All", value: SelectionFilter.ALL_ITEMS, control: _jsx(Radio, { size: "small" }) }, "all"), _jsx(Tooltip, { title: "Select devices first!", arrow: true, disableFocusListener: selectedGateway.length > 0, disableTouchListener: selectedGateway.length > 0, disableHoverListener: selectedGateway.length > 0, children: _jsx("span", { children: _jsx(FormControlLabel, { label: "Selected", value: SelectionFilter.SELECTED_ITEMS, control: _jsx(Radio, { size: "small" }), disabled: selectedGateway.length === 0 }, "selected") }) })] })),
        TimePeriodSelector: _jsx("div", { children: timeSelectorComponents.TimePeriodSelector }), // Have to wrap in div or it crashes :/
        TimePeriodComponents: timeSelectorComponents,
        PeriodOrRecent: durationMode,
        EveryNth: (_jsx(DebouncedTextField, { value: everyNth, onChange: (e) => {
                if (e.target.value === '') {
                    setEveryNth(undefined);
                }
                const newNth = parseFloat(e.target.value);
                if (isNaN(newNth)) {
                    return;
                }
                setEveryNth(newNth);
            }, size: "small", label: "Every Nth" })),
        // GoogleExport: (
        //   <>
        //     GExport TBC
        //     {/* <SimpleIOModal
        //     key={'GoogleExport'}
        //     {...{
        //       allowIOTypeToggle: false,
        //       allowCreate: allowCreateSheets,
        //       initialIOType: IOType.EXPORT,
        //       columnIndexedKeys: headers,
        //       plainObjects: deviceMessages
        //         .filter((d, idx) => (everyNth ? everyNth > 1 && idx % everyNth === 0 : true))
        //         .map(m => ({ type: 'DeviceMessage', ...getFlatLCDDeviceMessageForExport(m) })),
        //       setPlainObjectsInParent: () => {},
        //     }}
        //   /> */}
        //   </>
        // ),
        ColumnVisibiltyControls,
        deviceMessages,
        displayMode,
        liveDataFilter,
    };
};
